/**
 * ChainWalkOverlay — full-screen animated proof that the audit chain is
 * intact. Triggered when the user clicks "Verify chain" on the Audit
 * Log page. While the server-side `/api/audit/verify` request is in
 * flight, this overlay walks through ~12 sampled entries visually,
 * computing/displaying their hash neighbours and ticking each one off.
 *
 * Once both (a) the animation completes AND (b) the server returns
 * `valid:true`, the overlay flips to a big green "Tamper-evident" final
 * reveal with the head hash + entry count. If the server returns
 * `valid:false` or errors, we surface that immediately and stop the
 * animation.
 */
import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle, ShieldCheck, Hash, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";

const STEP_MS = 240; // per-entry animation cadence

export default function ChainWalkOverlay({
  open,
  onClose,
  entries,
  status,        // 'verifying' | 'verified' | 'broken' | 'error' | 'idle'
  verifyResult,  // { valid, entriesChecked, headHash, ... } | null
  verifyError,
}) {
  const sample = useMemo(() => sampleEntries(entries), [entries]);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open || sample.length === 0) return undefined;
    const t = setInterval(() => {
      setStep((s) => (s < sample.length ? s + 1 : s));
    }, STEP_MS);
    return () => {
      clearInterval(t);
      setStep(0);
    };
  }, [open, sample.length]);

  if (!open) return null;

  const finished = step >= sample.length;
  const settled = finished && (status === "verified" || status === "broken" || status === "error");
  const valid = status === "verified" && verifyResult?.valid;
  const totalChecked = verifyResult?.entriesChecked || entries.length;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      data-testid="audit-chain-walk-overlay"
    >
      <div className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[3px]" />
      <div className="relative w-full max-w-xl rounded-lg border border-border-subtle bg-raised shadow-e3 overflow-hidden">
        <div className="px-5 py-3 border-b border-border-subtle flex items-center justify-between bg-tralenex-50">
          <div className="flex items-center gap-2">
            <ShieldCheck size={15} className="text-tralenex-700" />
            <p className="eyebrow text-tralenex-700">Audit chain · live verification</p>
          </div>
          {settled && (
            <button
              type="button"
              onClick={onClose}
              className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-tralenex-100"
              data-testid="audit-chain-walk-close"
              aria-label="Close"
            >
              <X size={14} className="text-ink-secondary" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {!settled && (
            <>
              <p className="text-sm text-ink-secondary mb-3">
                Recomputing SHA-256 hash of each entry and matching against
                the previous block&apos;s digest. Walking <strong>{sample.length}</strong> sampled
                entries of <strong>{entries.length}</strong>.
              </p>
              <ul className="space-y-1.5 max-h-[280px] overflow-y-auto">
                {sample.map((e, i) => (
                  <ChainWalkRow
                    key={e.id}
                    entry={e}
                    state={i < step ? "ok" : i === step ? "checking" : "pending"}
                  />
                ))}
              </ul>
              <div className="mt-3 h-1.5 rounded bg-inset overflow-hidden">
                <div
                  className="h-full bg-tralenex-600 transition-all duration-200"
                  style={{ width: `${(step / sample.length) * 100}%` }}
                />
              </div>
              <p className="text-meta text-ink-disabled mt-1.5 inline-flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" />
                {finished ? "Server is sealing the result…" : `Verifying ${step} of ${sample.length}`}
              </p>
            </>
          )}

          {settled && (valid ? (
            <FinalSuccess totalChecked={totalChecked} headHash={verifyResult?.headHash || entries[0]?.hash} />
          ) : (
            <FinalFailure error={verifyError} status={status} />
          ))}
        </div>
      </div>
    </div>
  );
}

function sampleEntries(entries) {
  if (!entries || entries.length === 0) return [];
  // Always include genesis (last) + head (first); sample the middle.
  const SAMPLE_TARGET = 10;
  if (entries.length <= SAMPLE_TARGET) return entries.slice().reverse();
  const head = entries[0];
  const genesis = entries[entries.length - 1];
  const middleCount = SAMPLE_TARGET - 2;
  const stride = Math.floor((entries.length - 2) / middleCount);
  const middle = [];
  for (let i = 1; i <= middleCount; i++) {
    const idx = i * stride;
    middle.push(entries[idx]);
  }
  return [genesis, ...middle.reverse(), head];
}

function ChainWalkRow({ entry, state }) {
  return (
    <li className="flex items-center gap-2.5 px-2 py-1.5 rounded transition-colors bg-inset/40">
      {state === "ok" && <CheckCircle2 size={14} className="text-risk-low shrink-0" />}
      {state === "checking" && <Loader2 size={14} className="text-tralenex-700 animate-spin shrink-0" />}
      {state === "pending" && <span className="h-3.5 w-3.5 rounded-full border border-border-subtle shrink-0" />}
      <span className="font-mono text-meta tabular-nums text-ink-secondary w-[110px] shrink-0 truncate">
        {entry.id}
      </span>
      <span className={cn(
        "font-mono text-[10px] truncate flex-1",
        state === "ok" ? "text-ink-primary" : "text-ink-disabled",
      )}>
        {(entry.hash || "").slice(0, 16)}…
      </span>
      <span className="text-meta text-ink-disabled shrink-0">
        {state === "ok" ? "✓ matched" : state === "checking" ? "recomputing…" : "pending"}
      </span>
    </li>
  );
}

function FinalSuccess({ totalChecked, headHash }) {
  return (
    <div className="text-center py-3" data-testid="audit-chain-walk-success">
      <div className="inline-flex h-14 w-14 rounded-full bg-risk-low/10 items-center justify-center mx-auto">
        <ShieldCheck size={28} className="text-risk-low" />
      </div>
      <h3 className="text-h2 text-ink-primary mt-3">Chain verified · Tamper-evident</h3>
      <p className="text-sm text-ink-secondary mt-1">
        All <strong>{totalChecked}</strong> entries hash-chain back to genesis without break.
      </p>
      {headHash && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-inset font-mono text-meta">
          <Hash size={11} className="text-ink-secondary" /> head · {headHash.slice(0, 24)}…
        </div>
      )}
    </div>
  );
}

function FinalFailure({ error, status }) {
  return (
    <div className="text-center py-3" data-testid="audit-chain-walk-failure">
      <div className="inline-flex h-14 w-14 rounded-full bg-risk-high/10 items-center justify-center mx-auto">
        <AlertTriangle size={28} className="text-risk-high" />
      </div>
      <h3 className="text-h2 text-ink-primary mt-3">
        {status === "broken" ? "Chain integrity broken" : "Verification failed"}
      </h3>
      <p className="text-sm text-ink-secondary mt-1">
        {error || "A hash mismatch was detected. Open the audit drawer for the affected entry."}
      </p>
    </div>
  );
}
